/**
 * presetModalityCore.js — КАКИЕ 3D-ПРЕСЕТЫ ДОСТУПНЫ ДЛЯ ОТКРЫТОЙ СЕРИИ. [12.0.34]
 *
 * Чистый модуль, под тестами. Проводка — ui/presetModality.js.
 *
 * Владелец: «у меня все пресеты для КТ и МРТ в одной куче, нужно их как-то
 * разграничить: при запуске МРТ пресеты для КТ сделать серыми и неактивными,
 * и наоборот». Пресет КТ — это числа Хаунсфилда; у МРТ таких единиц нет, и
 * пресет по ним показывает не то. Наоборот так же.
 *
 * Модальность пресета берётся из самого пресета (presets3d.ts, поле
 * modality): 'ct', 'mri' или 'any'. «any» — пресеты, которые программа умеет
 * переносить на шкалу серии (planPresetRange): они доступны при любой
 * открытой серии.
 *
 * [12.0.35] ИССЛЕДОВАНИЯ НЕТ — ГАСНУТ ВСЕ. В 12.0.34 «серии нет» значило
 * «ничего не гасим», и после закрытия исследования все пресеты становились
 * активными. Это было неверно: без снимка пресету не к чему применяться, и
 * кнопка, которая нажимается и ничего не делает, врёт. Теперь при запуске
 * программы и после закрытия исследования серые все пресеты, универсальные
 * тоже.
 *
 * Три состояния серии, и их нельзя смешивать:
 *   null          — исследования нет: гаснут ВСЕ;
 *   'неизвестно'  — серия открыта, но модальность не записана: не гасим
 *                   НИЧЕГО (не знаем — не отнимаем кнопку у настоящего снимка);
 *   'ct' | 'mri' | 'иное' — серия открыта, модальность известна.
 * Серия без тега НЕ равна «исследования нет»: иначе на настоящем снимке
 * погасло бы всё. Поэтому модальностьСерии никогда не возвращает null — null
 * передаёт только проводка, когда исследования действительно нет.
 *
 * EXPORTS: модальностьСерии, доступностьПресета, ПОДПИСЬ, НЕИЗВЕСТНО,
 *          ПРИЧИНА_НЕТ_ИССЛЕДОВАНИЯ
 */

/** Как назвать модальность врачу. */
export const ПОДПИСЬ = Object.freeze({ ct: 'КТ', mri: 'МРТ', иное: 'эта серия' });

/** [12.0.35] Серия открыта, а модальность в ней не записана. */
export const НЕИЗВЕСТНО = 'неизвестно';

/** [12.0.35] Подсказка погашенной кнопки, когда исследование не открыто. */
export const ПРИЧИНА_НЕТ_ИССЛЕДОВАНИЯ = 'Исследование не открыто — пресету не к чему применяться';

/**
 * Значения, которые загрузчик подставляет сам, когда тега (0008,0060) нет:
 * workerPool пишет 'OT', сводка серий loaderManager — '?'. Для правила это
 * то же, что пустой тег: модальность не известна. [12.0.35]
 *
 * Цена: настоящая серия с модальностью OT («прочее») тоже считается
 * неизвестной, и пресеты КТ/МРТ на ней не гаснут. Это вторичные снимки и
 * скриншоты — объёма у них нет, пресет к ним и так не применится; лишняя
 * активная кнопка тут безвредна, а погасшие пресеты на КТ без тега — нет.
 */
const ЗАГЛУШКИ = new Set(['OT', '?']);

/**
 * Модальность ОТКРЫТОЙ серии из DICOM (0008,0060) → 'ct' | 'mri' | 'иное' | 'неизвестно'.
 *
 * [12.0.35] Никогда не null. Пустой тег, 'OT' и '?' — 'неизвестно'. Прежде
 * пустой тег давал null, и тем же null проводка сообщала «исследования нет»:
 * два разных случая были одним значением.
 */
export function модальностьСерии(dicom) {
  const м = String(dicom ?? '').trim().toUpperCase();
  if (!м || ЗАГЛУШКИ.has(м)) return НЕИЗВЕСТНО;
  if (м === 'CT') return 'ct';
  if (м === 'MR') return 'mri';
  return 'иное';
}

/**
 * Доступен ли пресет для серии.
 *
 * @param {string|null} модальностьПресета — 'ct' | 'mri' | 'any' | null (неизвестно)
 * @param {'ct'|'mri'|'иное'|'неизвестно'|null} серия — из модальностьСерии;
 *   null — исследование не открыто
 * @returns {{доступен:boolean, причина?:string}}
 */
export function доступностьПресета(модальностьПресета, серия) {
  // [12.0.35] Исследования нет — гаснут все, универсальные тоже.
  if (серия === null || серия === undefined) return { доступен: false, причина: ПРИЧИНА_НЕТ_ИССЛЕДОВАНИЯ };
  const п = String(модальностьПресета || '').toLowerCase();
  // Модальность серии или пресета неизвестна, пресет универсальный — не
  // гасим: лучше лишняя кнопка, чем пропавшая нужная.
  if (серия === НЕИЗВЕСТНО || !п || п === 'any') return { доступен: true };
  if (п === серия) return { доступен: true };
  const для = ПОДПИСЬ[п] || п;
  const открыта = серия === 'иное' ? 'открыта не КТ и не МРТ' : `открыта ${ПОДПИСЬ[серия]}`;
  return { доступен: false, причина: `Пресет для ${для} — ${открыта}` };
}
